import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CalendarDays } from "lucide-react";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";
import { fetchBlogPosts } from "@/lib/api";

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
};

export default function BlogListPage() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBlogPosts()
            .then(setPosts)
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, []);

    const blogLd = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Finance Wealth Blog",
        "url": "https://financewealth.com.au/blog",
        "publisher": { "@id": "https://financewealth.com.au/#business" }
    };

    return (
        <div data-testid="page-blog" className="pt-20">
            <Seo
                title="Home Loan & Mortgage Blog | Finance Wealth"
                description="Expert guides on home loans, refinancing, first home buyer grants and property investment for Australian borrowers — from mortgage broker Gurpreet Singh."
                keywords="mortgage blog Australia, home loan tips, first home buyer guide, refinancing advice Australia"
                jsonLd={blogLd}
            />
            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="overline">Insights &amp; Guides</div>
                        <h1 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02] text-[#1A362B] font-medium">
                            The Finance Wealth blog.
                        </h1>
                        <div className="divider-thin my-8" />
                        <p className="text-lg text-[#1C1C1C]/75 leading-relaxed font-light">
                            Practical home loan, refinancing and property finance advice for Australian borrowers.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-24">
                    {loading ? (
                        <div className="py-20 text-center text-[#1C1C1C]/50"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
                    ) : posts.length === 0 ? (
                        <div className="py-20 text-center text-[#1C1C1C]/60">No articles yet — check back soon.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {posts.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`blog-card-${p.slug}`}
                                    className="group flex flex-col bg-white border border-border rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                                >
                                    {p.cover_image && (
                                        <div className="aspect-[16/10] overflow-hidden bg-[#EBE8DF]">
                                            <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="p-7 flex flex-col flex-1">
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C69C6D]">{p.category}</span>
                                            <span className="text-[#1C1C1C]/40 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmt(p.created_at)}</span>
                                        </div>
                                        <h2 className="mt-4 font-serif text-2xl text-[#1A362B] tracking-tight leading-snug">{p.title}</h2>
                                        <p className="mt-3 text-sm text-[#1C1C1C]/70 leading-relaxed flex-1">{p.excerpt}</p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#1A362B]">
                                            Read article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <CtaStrip />
        </div>
    );
}
